import { forwardRef, type ReactNode } from 'react';
import { HelpTooltip } from '../../../shared/components/HelpTooltip';
import '../../job-roles/job-roles.css';
import '../benefits-deductions.css';

/**
 * Small labelled form controls for the benefit and deduction forms. Every control has a visible label, a hint and error text wired with
 * aria-describedby, `aria-invalid` when it has an error, and an optional "i" help trigger (HelpTooltip) with a plain-language explanation. Messages
 * never echo what was typed.
 */

interface BaseProps {
  id: string;
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  /** Plain-language explanation shown by the "i" trigger next to the label. */
  help?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  wide?: boolean | undefined;
}

function Shell({ id, label, error, hint, help, required, wide, children }: BaseProps & { children: ReactNode }) {
  return (
    <div className={wide ? 'jr-field jr-grid__wide' : 'jr-field'}>
      <div className="bd-label-row">
        <label htmlFor={id}>{label}{required && <span className="jr-req" aria-hidden="true"> *</span>}{required && <span className="jr-sr-only"> (required)</span>}</label>
        {help && <HelpTooltip label={`About ${label}`} text={help} />}
      </div>
      {children}
      {hint && <p id={`${id}-hint`} className="jr-hint">{hint}</p>}
      {error && <p id={`${id}-error`} className="jr-error" role="alert">{error}</p>}
    </div>
  );
}

function describedBy(id: string, error?: string, hint?: string): string | undefined {
  const parts = [hint ? `${id}-hint` : '', error ? `${id}-error` : ''].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : undefined;
}

export const TextInput = forwardRef<HTMLInputElement, BaseProps & {
  value: string; onChange: (value: string) => void; type?: 'text' | 'date' | 'search'; inputMode?: 'decimal' | 'numeric'; maxLength?: number; autoFocus?: boolean;
}>(function TextInput({ value, onChange, type = 'text', inputMode, maxLength, autoFocus, ...base }, ref) {
  return (
    <Shell {...base}>
      <input
        ref={ref}
        id={base.id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={base.disabled}
        required={base.required}
        aria-invalid={base.error ? true : undefined}
        aria-describedby={describedBy(base.id, base.error, base.hint)}
        {...(inputMode ? { inputMode } : {})}
        {...(maxLength ? { maxLength } : {})}
        {...(autoFocus ? { 'data-autofocus': true } : {})}
        autoComplete="off"
      />
    </Shell>
  );
});

export function TextAreaInput({ value, onChange, rows = 3, maxLength, ...base }: BaseProps & { value: string; onChange: (value: string) => void; rows?: number; maxLength?: number }) {
  return (
    <Shell {...base} wide>
      <textarea
        id={base.id}
        value={value}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
        disabled={base.disabled}
        aria-invalid={base.error ? true : undefined}
        aria-describedby={describedBy(base.id, base.error, base.hint)}
        {...(maxLength ? { maxLength } : {})}
      />
    </Shell>
  );
}

export interface Option {
  value: string;
  label: string;
}

export const SelectInput = forwardRef<HTMLSelectElement, BaseProps & { value: string; onChange: (value: string) => void; options: Option[] }>(function SelectInput(
  { value, onChange, options, ...base },
  ref,
) {
  return (
    <Shell {...base}>
      <select
        ref={ref}
        id={base.id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={base.disabled}
        required={base.required}
        aria-invalid={base.error ? true : undefined}
        aria-describedby={describedBy(base.id, base.error, base.hint)}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Shell>
  );
});

export function CheckboxInput({ id, label, checked, onChange, disabled, hint, help }: {
  id: string; label: string; checked: boolean; onChange: (value: boolean) => void; disabled?: boolean; hint?: string; help?: string;
}) {
  return (
    <div className="jr-field jr-grid__wide">
      <div className="bd-label-row">
        <label className="bd-check" htmlFor={id}>
          <input id={id} type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} aria-describedby={hint ? `${id}-hint` : undefined} />
          {label}
        </label>
        {help && <HelpTooltip label={`About ${label}`} text={help} />}
      </div>
      {hint && <p id={`${id}-hint`} className="jr-hint">{hint}</p>}
    </div>
  );
}

/** A related record the form needs (a tax profile, a deduction, a benefit plan) with an "Add" button that opens the canonical create dialog over this one,
 * so the person never loses what they typed (the product principle: a dependency can be added in place). Without a create action it says who can. */
export const RelatedSelect = forwardRef<HTMLSelectElement, BaseProps & {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  onAdd?: (() => void) | undefined;
  addLabel?: string;
  noAddNote?: string | undefined;
}>(function RelatedSelect({ onAdd, addLabel, noAddNote, ...selectProps }, ref) {
  return (
    <div className="jr-field">
      <div className="bd-inline-controls">
        <SelectInput ref={ref} {...selectProps} />
        {onAdd && (
          <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={onAdd} disabled={selectProps.disabled}>
            <span aria-hidden="true">+ </span>{addLabel ?? 'Add'}
          </button>
        )}
      </div>
      {!onAdd && noAddNote && <p className="jr-hint">{noAddNote}</p>}
    </div>
  );
});
